import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Moon, Sun, Globe, Menu, X, Linkedin, MessageCircle, Mail, Phone, MapPin, ALargeSmall, ChevronUp, Shield, ChevronDown } from "lucide-react";
const ChatWidget = lazy(() => import("@/components/chat-widget"));
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import logoFull from "@assets/logo-full.webp";
import { serviceConfig } from "@/lib/service-config";

export default function Layout({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const [location, setLocation] = useLocation();
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [isScrolled, setIsScrolled] = useState(false);
  const fontSizes = [100, 115, 130] as const;
  const fontLabels = ["A", "A+", "A++"] as const;
  const [fontIndex, setFontIndex] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [cookieConsent, setCookieConsent] = useState<"accepted" | "declined" | null>(() => {
    const stored = localStorage.getItem("gdpr-consent");
    return stored === "accepted" || stored === "declined" ? stored : null;
  });

  const handleConsent = (choice: "accepted" | "declined") => {
    localStorage.setItem("gdpr-consent", choice);
    setCookieConsent(choice);
  };

  const cycleFontSize = () => {
    setFontIndex(prev => {
      const next = (prev + 1) % fontSizes.length;
      document.documentElement.style.fontSize = `${fontSizes[next]}%`;
      return next;
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    const currentLang = i18n.language || "en";
    document.documentElement.dir = currentLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const changeLanguage = async (lng: string) => {
    const { loadLanguage } = await import("@/lib/i18n");
    await loadLanguage(lng);
    i18n.changeLanguage(lng);
  };

  const navLinks = [
    { href: "/", label: t("nav.home"), type: "page" as const },
    { href: "/about", label: t("nav.about"), type: "page" as const },
    { href: "/why-sla", label: t("nav.whyUs"), type: "page" as const },
    { href: "/services", label: t("nav.services"), type: "page" as const, hasDropdown: true },
    { href: "/approach", label: t("nav.process"), type: "page" as const },
    { href: "/success-stories", label: t("nav.success"), type: "page" as const },
    { href: "/team", label: t("nav.team"), type: "page" as const },
    { href: "/insights", label: t("nav.insights"), type: "page" as const },
    { href: "/locations", label: t("nav.locations"), type: "page" as const },
  ];

  const isNavActive = (link: { href: string; type: "page" }) => {
    if (link.href === "/") return location === "/";
    return location === link.href || location.startsWith(link.href + "/");
  };

  const navigateToLink = (link: { href: string; type: "page" }) => {
    setLocation(link.href);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-black text-white shadow-md`}
      >
        <div className="w-full px-4 h-28 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 h-full py-2 ml-0">
            <img 
              src={logoFull} 
              alt="Simon Lee & Associates" 
              width={800}
              height={353}
              className="h-full w-auto object-contain max-w-[280px] md:max-w-md"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) => (
              'hasDropdown' in link && link.hasDropdown ? (
                <div
                  key={link.href}
                  className="relative group"
                  onMouseEnter={() => setServicesDropdownOpen(true)}
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                >
                  <button
                    onClick={() => { setServicesDropdownOpen(!servicesDropdownOpen); }}
                    className={`text-sm font-medium transition-all whitespace-nowrap relative pb-1 flex items-center gap-1 ${
                      isNavActive(link) || location.startsWith("/services/")
                        ? "text-primary"
                        : "text-white/90 hover:text-white"
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`h-3 w-3 transition-transform ${servicesDropdownOpen ? "rotate-180" : ""}`} />
                    {(isNavActive(link) || location.startsWith("/services/")) && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                    )}
                  </button>
                  {servicesDropdownOpen && (
                    <div className="absolute top-full left-0 pt-2 z-50">
                      <div className="bg-popover border border-border rounded-lg shadow-lg py-1 w-64 animate-in fade-in-0 zoom-in-95 duration-150">
                        <button
                          onClick={() => { setServicesDropdownOpen(false); setLocation("/services"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          className="w-full text-left px-3 py-2 text-sm font-semibold hover:bg-accent hover:text-accent-foreground transition-colors rounded-sm mx-1"
                          style={{ width: "calc(100% - 8px)" }}
                        >
                          {t("services.viewAll")}
                        </button>
                        <div className="h-px bg-border my-1" />
                        {serviceConfig.map((service) => (
                          <button
                            key={service.slug}
                            onClick={() => { setServicesDropdownOpen(false); setLocation(`/services/${service.slug}`); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors rounded-sm mx-1 ${
                              location === `/services/${service.slug}` ? "text-primary" : ""
                            }`}
                            style={{ width: "calc(100% - 8px)" }}
                          >
                            {t(`services.${service.key}`)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={link.href}
                  onClick={() => navigateToLink(link)}
                  className={`text-sm font-medium transition-all whitespace-nowrap relative pb-1 ${
                    isNavActive(link)
                      ? "text-primary"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {link.label}
                  {isNavActive(link) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </button>
              )
            ))}

            <div className="flex items-center gap-2 border-l pl-4 border-white/20">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full text-white hover:bg-white/10 hover:text-white" aria-label="Select language">
                    <Globe className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => changeLanguage("en")}>
                    English (US)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("es")}>
                    Español
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("ar")}>
                    العربية (Saudi)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("fr")}>
                    Français
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="icon"
                onClick={cycleFontSize}
                className="rounded-full text-white hover:bg-white/10 hover:text-white relative"
                data-testid="button-font-toggle"
                title={`Font size: ${fontLabels[fontIndex]}`}
                aria-label={`Change font size, currently ${fontLabels[fontIndex]}`}
              >
                <ALargeSmall className="h-4 w-4" />
                {fontIndex > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 text-[9px] font-bold bg-primary text-white rounded-full h-3.5 w-3.5 flex items-center justify-center">
                    {fontIndex === 1 ? "+" : "++"}
                  </span>
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full text-white hover:bg-white/10 hover:text-white"
                aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              >
                {theme === "light" ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </Button>

              <Button size="sm" onClick={() => { setLocation("/contact"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="bg-primary text-white hover:bg-primary/90 border-none whitespace-nowrap">
                {t("nav.getStarted")}
              </Button>
            </div>
          </nav>

          {/* Mobile Nav */}
          <div className="xl:hidden flex items-center gap-4">
             <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-white hover:bg-white/10" aria-label="Select language">
                    <Globe className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => changeLanguage("en")}>
                    EN
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("es")}>
                    ES
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("ar")}>
                    AR
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("fr")}>
                    FR
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="icon"
                onClick={cycleFontSize}
                className="rounded-full h-8 w-8 text-white hover:bg-white/10 relative"
                aria-label="Change font size"
              >
                <ALargeSmall className="h-4 w-4" />
                {fontIndex > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 text-[9px] font-bold bg-primary text-white rounded-full h-3.5 w-3.5 flex items-center justify-center">
                    {fontIndex === 1 ? "+" : "++"}
                  </span>
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full h-8 w-8 text-white hover:bg-white/10"
                aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              >
                {theme === "light" ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </Button>
            </div>

            <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) setMobileServicesOpen(false); }}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" aria-label="Open navigation menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto">
                <nav className="flex flex-col gap-4 mt-10">
                  {navLinks.map((link) => (
                    'hasDropdown' in link && link.hasDropdown ? (
                      <div key={link.href}>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setSheetOpen(false); setMobileServicesOpen(false); setLocation("/services"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                            className={`text-lg font-medium transition-colors text-left px-3 py-1.5 rounded-lg flex-1 ${
                              isNavActive(link) || location.startsWith("/services/")
                                ? "text-primary bg-primary/10"
                                : "hover:text-primary"
                            }`}
                            data-testid="link-mobile-services"
                          >
                            {link.label}
                          </button>
                          <button
                            onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                            className="p-2 rounded-lg hover:bg-accent transition-colors"
                            data-testid="button-mobile-services-toggle"
                            aria-label={mobileServicesOpen ? "Collapse services" : "Expand services"}
                          >
                            <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                          </button>
                        </div>
                        {mobileServicesOpen && (
                          <div className="ml-4 mt-1 flex flex-col gap-1 animate-in slide-in-from-top-2 duration-200">
                            {serviceConfig.map((service) => (
                              <button
                                key={service.slug}
                                onClick={() => { setSheetOpen(false); setMobileServicesOpen(false); setLocation(`/services/${service.slug}`); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                className={`text-sm transition-colors text-left px-3 py-1.5 rounded-lg ${
                                  location === `/services/${service.slug}`
                                    ? "text-primary bg-primary/5"
                                    : "text-muted-foreground hover:text-primary"
                                }`}
                                data-testid={`link-mobile-service-${service.slug}`}
                              >
                                {t(`services.${service.key}`)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        key={link.href}
                        onClick={() => { setSheetOpen(false); navigateToLink(link); }}
                        className={`text-lg font-medium transition-colors text-left px-3 py-1.5 rounded-lg ${
                          isNavActive(link)
                            ? "text-primary bg-primary/10"
                            : "hover:text-primary"
                        }`}
                      >
                        {link.label}
                      </button>
                    )
                  ))}
                  <Button className="w-full mt-4" onClick={() => { setSheetOpen(false); setLocation("/contact"); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                    {t("nav.getStarted")}
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {children}
      </main>

      {location === "/" && <Suspense fallback={null}><ChatWidget /></Suspense>}

      {isScrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className={`fixed ${cookieConsent === null ? "bottom-44 md:bottom-28" : "bottom-4"} right-4 z-50 h-10 w-10 rounded-full bg-primary/80 hover:bg-primary text-primary-foreground shadow-lg flex items-center justify-center transition-all duration-300 animate-in fade-in slide-in-from-bottom-4`}
          data-testid="button-back-to-top"
          aria-label="Back to top"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      )}

      {cookieConsent === null && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-2xl animate-in slide-in-from-bottom-6 duration-500" data-testid="gdpr-banner">
          <div className="container mx-auto px-4 py-4 md:py-5">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold mb-1">{t("gdpr.title")}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t("gdpr.message")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 w-full md:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleConsent("declined")}
                  data-testid="button-gdpr-decline"
                >
                  {t("gdpr.decline")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleConsent("accepted")}
                  data-testid="button-gdpr-accept"
                >
                  {t("gdpr.accept")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="bg-muted py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <Link href="/" className="flex items-center gap-2 mb-4">
                <img 
                  src={logoFull} 
                  alt="Simon Lee & Associates" 
                  width={800}
                  height={353}
                  className="h-36 w-auto object-contain"
                />
              </Link>
              <p className="text-muted-foreground max-w-sm">
                {t("hero.description")}
              </p>
            </div>
            
            <div>
              <h2 className="font-semibold mb-4 text-base">{t("nav.contact")}</h2>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:simon@simonleeandassociates.com" className="hover:text-primary transition-colors">
                    simon@simonleeandassociates.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a href="tel:+13145622900" className="hover:text-primary transition-colors">
                    (314) 562-2900
                  </a>
                </li>
                 <li className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  <a href="https://wa.me/13145622900" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    {t("contact.whatsapp")}: (314) 562-2900
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>{t("contact.location")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4" />
                  <a href="https://www.linkedin.com/company/108036011" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    {t("contact.linkedinFollow")}
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h2 className="font-semibold mb-4 text-base">{t("footer.linksTitle")}</h2>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link href="/about" className="hover:text-primary" data-testid="link-footer-about">{t("nav.about")}</Link></li>
                <li><Link href="/services" className="hover:text-primary" data-testid="link-footer-services">{t("nav.services")}</Link></li>
                <li><Link href="/approach" className="hover:text-primary" data-testid="link-footer-approach">{t("nav.process")}</Link></li>
                <li><Link href="/success-stories" className="hover:text-primary" data-testid="link-footer-success">{t("nav.success")}</Link></li>
                <li><Link href="/team" className="hover:text-primary" data-testid="link-footer-team">{t("nav.team")}</Link></li>
                <li><Link href="/products" className="hover:text-primary" data-testid="link-footer-products">{t("products.title")}</Link></li>
                <li><Link href="/insights" className="hover:text-primary" data-testid="link-footer-insights">{t("insights.title")}</Link></li>
                <li><Link href="/blog" className="hover:text-primary" data-testid="link-footer-blog">{t("blog.title")}</Link></li>
                <li><Link href="/contact" className="hover:text-primary" data-testid="link-footer-contact">{t("nav.contact")}</Link></li>
                <li><Link href="/faq" className="hover:text-primary" data-testid="link-footer-faq">{t("faq.title")}</Link></li>
                <li><Link href="/privacy" className="hover:text-primary" data-testid="link-footer-privacy">{t("footer.privacy")}</Link></li>
                <li><Link href="/terms" className="hover:text-primary" data-testid="link-footer-terms">{t("footer.terms")}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-8 mt-8">
            <div className="text-center text-sm text-muted-foreground mb-4">
              © {new Date().getFullYear()} Simon Lee & Associates, LLC. {t("footer.rights")}
            </div>
            
            <div className="max-w-4xl mx-auto" onMouseLeave={() => (document.querySelector('[data-state="open"] button') as HTMLButtonElement | null)?.click()}>
              <Accordion type="single" collapsible className="w-full border-none">
                <AccordionItem value="main-disclaimer" className="border-none">
                  <AccordionTrigger className="justify-center text-xs text-muted-foreground hover:text-primary py-2 font-medium">
                    {t("footer.disclaimerTitle")}
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <Accordion type="single" collapsible className="w-full">
                      {[
                        "general", 
                        "professional", 
                        "legal", 
                        "confidentiality", 
                        "market", 
                        "liability", 
                        "thirdParty", 
                        "forward", 
                        "updates"
                      ].map((key) => (
                        <AccordionItem key={key} value={key} className="border-b border-border/50 last:border-0">
                          <AccordionTrigger className="text-sm font-medium hover:text-primary py-3 text-left">
                            {t(`footer.disclaimerItems.${key}.title`)}
                          </AccordionTrigger>
                          <AccordionContent className="text-sm text-foreground/80 text-left leading-relaxed pb-4">
                            {t(`footer.disclaimerItems.${key}.content`)}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                    
                    <div className="flex justify-center gap-4 pt-8 mt-4 border-t">
                      <Link href="/privacy" className="hover:text-primary transition-colors text-sm" data-testid="link-disclaimer-privacy">{t("footer.privacy")}</Link>
                      <span className="text-muted-foreground">•</span>
                      <Link href="/terms" className="hover:text-primary transition-colors text-sm" data-testid="link-disclaimer-terms">{t("footer.terms")}</Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
